/**
 * DKIM Selectors Section
 *
 * Extracted from domain detail page for use in the Configuration tab.
 * Manages DKIM selector tracking, validation, and usage data.
 */

import { useState, useEffect, useMemo } from "react";
import { toast } from "@/lib/toast";
import { useConfirm } from "@/hooks/useConfirm";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Key,
  RefreshCw,
  Trash2,
  Lock,
  Mail,
  Users,
} from "lucide-react";
import { api, DkimSelector, DkimSelectorUsageResponse } from "@/lib/api";
import { Modal } from "@/components/ui/Modal";

export function DkimSection({
  selectors,
  domainId,
  onRefresh,
}: {
  selectors: DkimSelector[];
  domainId: string;
  onRefresh: () => void;
}) {
  const { confirm } = useConfirm();
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<DkimSelectorUsageResponse | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [expandedSelector, setExpandedSelector] = useState<string | null>(null);

  useEffect(() => {
    fetchUsageData();
  }, [domainId]);

  async function fetchUsageData() {
    try {
      setLoadingUsage(true);
      const data = await api.getDkimSelectorUsage(domainId);
      setUsageData(data);
    } catch (error) {
      console.error("Failed to fetch DKIM usage data:", error);
    } finally {
      setLoadingUsage(false);
    }
  }

  async function handleDelete(selectorId: string) {
    const confirmed = await confirm({ title: "Delete DKIM Selector", message: "Are you sure you want to delete this DKIM selector?", variant: "danger" });
    if (!confirmed) return;

    try {
      setDeletingId(selectorId);
      await api.deleteDkimSelector(domainId, selectorId);
      onRefresh();
      fetchUsageData();
    } catch (error) {
      console.error("Failed to delete DKIM selector:", error);
      toast.error("Failed to delete DKIM selector");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleValidate(selectorId: string) {
    try {
      await api.validateDkimSelector(domainId, selectorId);
      onRefresh();
      fetchUsageData();
    } catch (error) {
      console.error("Failed to validate DKIM selector:", error);
      toast.error("Failed to validate DKIM selector");
    }
  }

  // Merge selector data with usage data
  const mergedSelectors = useMemo(() => {
    if (!usageData) return selectors.map(s => ({ ...s, usage: null }));

    return selectors.map(selector => {
      const usage = usageData.selectors.find(u => u.selector === selector.selector);
      return { ...selector, usage: usage || null };
    });
  }, [selectors, usageData]);

  // Get untracked selectors (seen in reports but not configured)
  const untrackedSelectors = useMemo(() => {
    if (!usageData) return [];
    return usageData.selectors.filter(u => u._untracked);
  }, [usageData]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          {usageData && (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500">
                <span className="font-medium text-slate-700">{usageData.summary.totalSelectors}</span> selectors tracked
              </span>
              <span className="text-emerald-700">
                <CheckCircle2 className="w-4 h-4 inline mr-1" />
                {usageData.summary.validSelectors} valid
              </span>
              {usageData.summary.invalidSelectors > 0 && (
                <span className="text-red-600">
                  <XCircle className="w-4 h-4 inline mr-1" />
                  {usageData.summary.invalidSelectors} invalid
                </span>
              )}
              {usageData.summary.untrackedSelectors > 0 && (
                <span className="text-amber-600">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  {usageData.summary.untrackedSelectors} untracked
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors"
        >
          <Key className="w-5 h-5" />
          Add DKIM Selector
        </button>
      </div>

      {selectors.length === 0 && untrackedSelectors.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Key className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            No DKIM selectors configured
          </h3>
          <p className="text-slate-500">
            Add DKIM selectors to monitor key rotation and validity.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Configured Selectors */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h3 className="font-semibold text-slate-800">Configured Selectors</h3>
              <p className="text-sm text-slate-500 mt-1">DKIM selectors you've added for monitoring</p>
            </div>
            {selectors.length === 0 ? (
              <div className="px-6 py-8 text-center text-slate-500">
                No configured selectors. Add selectors above to track them.
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {mergedSelectors.map((selector) => (
                  <div key={selector.id}>
                    <div
                      className="px-6 py-4 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setExpandedSelector(
                        expandedSelector === selector.id ? null : selector.id
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            {expandedSelector === selector.id ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                            <span className="font-mono text-sm font-medium">{selector.selector}</span>
                          </div>
                          {selector.is_valid ? (
                            <span className="flex items-center gap-1 text-emerald-700 text-sm">
                              <CheckCircle2 className="w-4 h-4" />
                              Valid
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600 text-sm">
                              <XCircle className="w-4 h-4" />
                              Invalid
                            </span>
                          )}
                          {selector.usage?.providerHint && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                              {selector.usage.providerHint}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-6">
                          {selector.usage && (
                            <div className="flex items-center gap-4 text-sm">
                              <span className="flex items-center gap-1 text-slate-600">
                                <Mail className="w-4 h-4" />
                                {selector.usage.usage.totalMessages.toLocaleString()} msgs
                              </span>
                              <span className={`font-medium ${
                                selector.usage.usage.passRate >= 95 ? 'text-emerald-700' :
                                selector.usage.usage.passRate >= 80 ? 'text-amber-600' :
                                'text-red-600'
                              }`}>
                                {selector.usage.usage.passRate.toFixed(1)}% pass
                              </span>
                              <span className="flex items-center gap-1 text-slate-600">
                                <Users className="w-4 h-4" />
                                {selector.usage.sources.length} sources
                              </span>
                            </div>
                          )}
                          {!selector.usage && !loadingUsage && (
                            <span className="text-sm text-slate-400">No usage data</span>
                          )}
                          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => handleValidate(selector.id)}
                              className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Validate"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(selector.id)}
                              disabled={deletingId === selector.id}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Source Details */}
                    {expandedSelector === selector.id && selector.usage && selector.usage.sources.length > 0 && (
                      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                        <h4 className="text-sm font-medium text-slate-700 mb-3">Sources Using This Selector</h4>
                        {/* Tier restriction banner */}
                        {usageData && !usageData.tierRestrictions.canViewSourceDetails && (
                          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4 text-amber-600" />
                              <span className="text-sm text-amber-800">
                                Upgrade to Starter to see full source details and hostnames
                              </span>
                            </div>
                            <Link
                              to="/settings/billing"
                              className="text-sm font-medium text-amber-700 hover:text-amber-900"
                            >
                              Upgrade
                            </Link>
                          </div>
                        )}
                        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-200 bg-slate-50">
                                <th className="text-left px-4 py-2 text-xs font-medium text-slate-500 uppercase">Source</th>
                                <th className="text-right px-4 py-2 text-xs font-medium text-slate-500 uppercase">Messages</th>
                                <th className="text-right px-4 py-2 text-xs font-medium text-slate-500 uppercase">Pass Rate</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {selector.usage.sources.map((source, idx) => (
                                <tr key={idx} className="hover:bg-slate-50">
                                  <td className="px-4 py-2">
                                    <div>
                                      <span className={`font-medium ${source.sourceIdentifier === "Upgrade to view" ? "text-slate-400 italic" : "text-slate-800"}`}>
                                        {source.sourceName || source.sourceIdentifier}
                                      </span>
                                      {source.sourceName && (
                                        <span className="text-slate-400 text-xs ml-2">{source.sourceIdentifier}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-right text-slate-600">
                                    {source.messageCount.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2 text-right">
                                    <span className={`font-medium ${
                                      source.passRate >= 95 ? 'text-emerald-700' :
                                      source.passRate >= 80 ? 'text-amber-600' :
                                      'text-red-600'
                                    }`}>
                                      {source.passRate.toFixed(1)}%
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {expandedSelector === selector.id && (!selector.usage || selector.usage.sources.length === 0) && (
                      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                        <p className="text-sm text-slate-500">
                          No sources have been seen using this selector in recent reports.
                          This selector may not be in active use or hasn't appeared in DMARC reports yet.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Untracked Selectors */}
          {untrackedSelectors.length > 0 && (
            <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-amber-200 bg-amber-50">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-slate-800">Untracked Selectors</h3>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  These selectors appeared in DMARC reports but aren't configured for monitoring.
                  Consider adding them to track their validity.
                </p>
              </div>
              <div className="divide-y divide-slate-200">
                {untrackedSelectors.map((selector) => (
                  <div key={selector.selector} className="px-6 py-4 hover:bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-sm font-medium">{selector.selector}</span>
                        {selector.providerHint && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                            {selector.providerHint}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <span className="flex items-center gap-1 text-slate-600">
                          <Mail className="w-4 h-4" />
                          {selector.usage.totalMessages.toLocaleString()} msgs
                        </span>
                        <span className={`font-medium ${
                          selector.usage.passRate >= 95 ? 'text-emerald-700' :
                          selector.usage.passRate >= 80 ? 'text-amber-600' :
                          'text-red-600'
                        }`}>
                          {selector.usage.passRate.toFixed(1)}% pass
                        </span>
                        <span className="flex items-center gap-1 text-slate-600">
                          <Users className="w-4 h-4" />
                          {selector.sources.length} sources
                        </span>
                      </div>
                    </div>
                    {selector.sources.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {selector.sources.slice(0, 5).map((source, idx) => (
                          <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                            {source.sourceName || source.sourceIdentifier}
                          </span>
                        ))}
                        {selector.sources.length > 5 && (
                          <span className="px-2 py-1 text-slate-400 text-xs">
                            +{selector.sources.length - 5} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <AddDkimModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        domainId={domainId}
        onSuccess={() => {
          setShowAddModal(false);
          onRefresh();
          fetchUsageData();
        }}
      />
    </div>
  );
}

function AddDkimModal({
  open,
  domainId,
  onClose,
  onSuccess,
}: {
  open: boolean;
  domainId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [selector, setSelector] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selector.trim()) return;

    try {
      setLoading(true);
      setError(null);
      await api.addDkimSelector(domainId, selector.trim());
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add selector");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()}>
      <Modal.Content title="Add DKIM Selector" size="md">
        <div className="p-4 pt-2">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="selector"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Selector Name
              </label>
              <input
                id="selector"
                type="text"
                placeholder="e.g., google, selector1"
                value={selector}
                onChange={(e) => setSelector(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                disabled={loading}
              />
            </div>
            {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors disabled:opacity-50"
                disabled={loading || !selector.trim()}
              >
                {loading ? "Adding..." : "Add Selector"}
              </button>
            </div>
          </form>
        </div>
      </Modal.Content>
    </Modal>
  );
}
